import type { ComponentType, SVGProps } from 'react'

import {
  ChatGPTIcon,
  ClaudeIcon,
  GeminiIcon,
  GrokIcon,
  PerplexityIcon,
} from '@/components/social-icons'
import { siteConfig } from '@/config/site'
import { cn } from '@/lib/utils'

/* One-click links that open popular AI assistants with a pre-filled prompt
   asking for an overview of Euclideum, sourced from this site.

   The visitor sees the text sitting in the chat input before they send it,
   so it reads as a buyer's evaluation question. Two versions of it exist:

     · PROMPT — the preferred wording, used by every assistant that answers
       it straight ("show me why X is worth considering … a great fit").

     · CLAUDE_PROMPT — Claude specifically recognises that wording as
       advocacy arriving from the company's own site, and prefaces its
       answer by calling the prompt out as marketing. Its variant keeps the
       same frame and the same asks, but requests the highlights *so the
       visitor can judge* the fit rather than telling the model the verdict
       ("worth considering", "great fit") up front — the one difference
       between a summary and a disclaimer. Keep the two in step if the copy
       ever changes. */

const EVALUATION_FRAME = `I'm evaluating technology partners for enterprise software, cloud infrastructure, and education technology.`

const PROMPT = encodeURIComponent(
  `${EVALUATION_FRAME} Show me why ${siteConfig.name} is worth considering. Please summarize the key highlights from their website ${siteConfig.url} that make it a great fit for my organization.`
)

const CLAUDE_PROMPT = encodeURIComponent(
  `${EVALUATION_FRAME} I'm looking at ${siteConfig.name}. Please summarize the key highlights from their website ${siteConfig.url} so I can judge whether they're a good fit for my organization.`
)

interface Assistant {
  name: string
  href: string
  Icon: ComponentType<SVGProps<SVGSVGElement>>
  /* Brand chip background + glyph color. ChatGPT and Grok are monochrome
     brands, so their chips flip with the theme via the semantic tokens. */
  chip: string
}

const ASSISTANTS: Assistant[] = [
  {
    name: 'ChatGPT',
    href: `https://chatgpt.com/?prompt=${PROMPT}`,
    Icon: ChatGPTIcon,
    chip: 'bg-foreground text-background',
  },
  {
    name: 'Perplexity',
    href: `https://www.perplexity.ai/search/new?q=${PROMPT}`,
    Icon: PerplexityIcon,
    chip: 'bg-[#20808D] text-white',
  },
  {
    name: 'Claude',
    href: `https://claude.ai/new?q=${CLAUDE_PROMPT}`,
    Icon: ClaudeIcon,
    chip: 'bg-[#D97757] text-white',
  },
  {
    name: 'Gemini',
    href: `https://www.google.com/search?udm=50&aep=11&q=${PROMPT}`,
    Icon: GeminiIcon,
    chip: 'bg-gradient-to-br from-[#4285F4] via-[#9B72CB] to-[#D96570] text-white',
  },
  {
    name: 'Grok',
    href: `https://x.com/i/grok?text=${PROMPT}`,
    Icon: GrokIcon,
    chip: 'bg-foreground text-background',
  },
]

export function AiSummary({ className }: { className?: string }) {
  return (
    <div
      className={cn('flex flex-wrap items-center gap-x-3 gap-y-2', className)}
    >
      <p className="text-xs text-muted-foreground dark:text-muted-foreground sm:text-sm">
        Get an AI summary of Euclideum
      </p>

      <ul className="flex items-center gap-2">
        {ASSISTANTS.map(({ name, href, Icon, chip }) => (
          <li key={name}>
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              title={name}
              aria-label={`Get an AI summary of Euclideum on ${name}`}
              className={cn(
                'flex size-6 items-center justify-center rounded-full ring-offset-background transition-transform duration-300 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 motion-reduce:transition-none motion-reduce:hover:transform-none',
                chip
              )}
            >
              <Icon aria-hidden className="size-3.5 shrink-0" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}
